/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';

/** True for a plain left-click with no modifier keys (let ctrl/cmd/middle-click behave normally). */
function isPlainClick(e: MouseEvent): boolean {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Run `handler` instead of navigating when a shopper clicks an anchor whose href is one of
 * `hrefs` — anywhere on the page, in any Page Designer component.
 *
 * Sentinel hrefs are how content-authored CTAs trigger JavaScript on this storefront: the link
 * picker stores a type (`chat`, `cookieSettings`) that resolves to a hash the browser can render as
 * a normal link, and a listener like this one turns the click into an action. They are never real
 * destinations.
 *
 * Capture phase plus `stopImmediatePropagation` means this wins before React Router's `<Link>`
 * click handler (and any component handler), so it works whether the CTA rendered a plain `<a>` or
 * a site-aware `<Link>` — both emit an `<a>` in the DOM.
 *
 * The handler is held in a ref so a caller can pass an inline function without re-installing the
 * listener on every render.
 *
 * @param hrefs raw `href` attribute values to intercept.
 * @param handler invoked with the clicked anchor once a sentinel href matches.
 */
export function useSentinelLink(hrefs: readonly string[], handler: (anchor: HTMLAnchorElement) => void): void {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    // Join rather than spread `hrefs` into the dependency list: callers pass an inline array
    // literal, which is a new reference on every render.
    const hrefKey = hrefs.join(' ');

    useEffect(() => {
        const sentinels = new Set(hrefKey.split(' '));

        function onClick(e: MouseEvent) {
            if (!isPlainClick(e)) return;
            const target = e.target as Element | null;
            const anchor = target?.closest?.('a[href]');
            if (!anchor) return;
            // Compare the raw attribute (not the resolved `.href`, which the browser absolutizes).
            const href = anchor.getAttribute('href');
            if (!href || !sentinels.has(href)) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            handlerRef.current(anchor as HTMLAnchorElement);
        }

        document.addEventListener('click', onClick, { capture: true });
        return () => document.removeEventListener('click', onClick, { capture: true });
    }, [hrefKey]);
}
